import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Database, RefreshCw } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Round 92o++++++++N++++++ (May 2026) — at-a-glance health pill for
// the US Market Universe collection. Shows total rows, non-dividend
// count, and how many are still unclassified. Tone shifts from rose
// (<25% classified) → amber (<60%) → emerald (≥60%) so the admin can
// tell at a glance whether the weekly Classify Non-Dividend job has
// caught up or fallen behind.
//
// Polls `/api/admin/us-market-universe/stats` once on mount and any
// time the user clicks the refresh icon. Listens for a global
// `universe-stats-changed` event so the warmer buttons can ping us
// after a successful run without us having to poll every few seconds.

export default function AdminUniverseHealthPill() {
  // Round P111u — self-gate; hides even if wrapper is bypassed.
  const { user: _authUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const { data } = await axios.get(
        `${API}/admin/us-market-universe/stats`,
        { withCredentials: true },
      );
      setStats(data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const h = () => { load(); };
    window.addEventListener('universe-stats-changed', h);
    return () => window.removeEventListener('universe-stats-changed', h);
  }, [load]);

  if (loading && !stats) {
    return (
      <div
        className="inline-flex items-center gap-1.5 text-[10px] font-semibold px-2 py-1 rounded-md border border-dashed border-muted-foreground/30 bg-muted/20 text-muted-foreground"
        data-testid="admin-universe-health-pill"
      >
        <Database className="w-3 h-3 animate-pulse" /> Universe: loading…
      </div>
    );
  }
  if (error) {
    return (
      <button
        type="button"
        onClick={load}
        className="inline-flex items-center gap-1.5 text-[10px] font-semibold px-2 py-1 rounded-md border border-dashed border-rose-400/40 bg-rose-50 dark:bg-rose-950/30 text-rose-700 dark:text-rose-300 hover:bg-rose-100"
        data-testid="admin-universe-health-pill"
        title="Click to retry"
      >
        <Database className="w-3 h-3" /> Universe: error · retry
      </button>
    );
  }
  if (!stats) return null;

  const total = stats.total || 0;
  const nonDiv = stats.total_non_dividend || 0;
  const unclassified = stats.total_unclassified || 0;
  const classified = total - unclassified;
  const pct = total > 0 ? (classified / total) * 100 : 0;
  // Empty universe → user hasn't run the refresh yet. Use neutral
  // tone instead of rose-tinted "panic" red so it doesn't scream.
  const tone = total === 0
    ? 'text-muted-foreground'
    : pct >= 60
      ? 'text-emerald-600 dark:text-emerald-400'
      : pct >= 25
        ? 'text-amber-600 dark:text-amber-400'
        : 'text-rose-600 dark:text-rose-400';
  const fmt = (n) => Number(n).toLocaleString();
  const byCls = stats.by_classification || {};
  const cs = byCls.common_stock?.count || 0;
  const etf = byCls.etf?.count || 0;
  const adr = byCls.adr?.count || 0;
  const tipLines = [
    `${fmt(total)} active US-listed securities in the universe.`,
    `${fmt(cs)} Common Stocks · ${fmt(etf)} ETFs · ${fmt(adr)} ADRs.`,
    `${fmt(classified)} classified (${pct.toFixed(0)}%) · ${fmt(unclassified)} pending.`,
    `${fmt(nonDiv)} confirmed as NON-dividend.`,
    `Click ↻ to refresh this pill from /admin/us-market-universe/stats.`,
  ];

  // Round P111u — self-gate (post-hooks; safe with Rules of Hooks).
  if (_authUser?.role !== 'admin') return null;

  return (
    <div
      className={`inline-flex items-center gap-1.5 text-[10px] font-semibold px-2 py-1 rounded-md border border-dashed border-muted-foreground/30 bg-muted/20`}
      title={tipLines.join('\n')}
      data-testid="admin-universe-health-pill"
    >
      <Database className={`w-3 h-3 ${tone}`} />
      <span>Universe:</span>
      <span className={`font-bold font-mono ${tone}`} data-testid="universe-health-total">
        {fmt(total)}
      </span>
      {total > 0 && (
        <>
          <span className="text-muted-foreground/70">·</span>
          <span className="font-mono" data-testid="universe-health-nondiv">
            <span className={tone}>{fmt(nonDiv)}</span>
            <span className="text-muted-foreground/70 ml-0.5">non-div</span>
          </span>
          {unclassified > 0 && (
            <>
              <span className="text-muted-foreground/70">·</span>
              <span className="font-mono text-muted-foreground" data-testid="universe-health-unclassified">
                {fmt(unclassified)} pending
              </span>
            </>
          )}
        </>
      )}
      <button
        type="button"
        onClick={load}
        className="ml-0.5 p-0.5 rounded hover:bg-muted text-muted-foreground"
        title="Refresh stats"
        data-testid="admin-universe-health-refresh"
      >
        <RefreshCw className={`w-2.5 h-2.5 ${loading ? 'animate-spin' : ''}`} />
      </button>
    </div>
  );
}
